import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React, { useEffect } from 'react';
import { Button, Row, Col } from 'react-bootstrap';
import Common from '../../shared/Common';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import type { IIncomeCalendarSynchronization } from '../../../interfaces/IIncomeCalendarSynchronization';
import { SortUpIcon, SortDownIcon } from '../../shared/icons/Svgs';

import './style.css';

export interface ITableProps {
  data: IIncomeCalendarSynchronization[];
  loading: boolean;
  fetchData: () => Promise<void>;
  fetchTrigger: number;
  setFetchTrigger: React.Dispatch<React.SetStateAction<number>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPerformModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRowId: number;
  setSelectedRowId: React.Dispatch<React.SetStateAction<number>>;
  selectedIncomeCalendarSynchronization: IIncomeCalendarSynchronization | null;
  setSelectedIncomeCalendarSynchronization: React.Dispatch<React.SetStateAction<IIncomeCalendarSynchronization | null>>;
}

function IncomeCalendarsSynchronizationsTable(props: ITableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const columns: ColumnDef<IIncomeCalendarSynchronization>[] = React.useMemo<
    ColumnDef<IIncomeCalendarSynchronization>[]
  >(
    () => [
      {
        accessorFn: (row) => row.name,
        id: 'name',
        header: () => <span>Nazwa</span>,
      },
      {
        accessorFn: (row) => row.url,
        id: 'url',
        header: () => <span>Url</span>,
      },
      {
        accessorFn: (row) => row.items?.length ?? 0,
        id: 'items',
        header: () => <span>Konfiguracje</span>,
      },
      {
        accessorFn: (row) => new Date(row.lastUpdate),
        id: 'lastUpdateDate',
        header: () => <span>Poprzednia aktualizacja</span>,
        cell: (info) => (
          <span>{Common.Utils.getDatetimeWithoutZoneConverter(info.row.original.lastUpdate?.toString())}</span>
        ),
      },
      {
        accessorFn: (row) => new Date(row.nextUpdate),
        id: 'nextUpdateDate',
        header: () => <span>Następna aktualizacja</span>,
        cell: (info) => (
          <span>
            {info.row.original.manualActivation ?
              <Button size='sm' variant='dark' onClick={(e: any) => {
                props.setSelectedRowId(info.row.index);
                props.setSelectedIncomeCalendarSynchronization(info.row.original);
                props.setShowPerformModal(true);
                e.stopPropagation();
              }}>Synchronizuj</Button> :
              info.row.original.active
                ? Common.Utils.getDatetimeWithoutZoneConverter(info.row.original.nextUpdate?.toString())
                : ''}
          </span>
        ),
      },
    ],
    [props]
  );

  const table = useReactTable({
    data: props.data,
    columns: columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { fetchData, fetchTrigger } = props;

  useEffect(() => {
    fetchData();
  }, [fetchData, fetchTrigger]);

  const onTableRowClick = (index: number, selectedIncomeCalendarSynchronization: IIncomeCalendarSynchronization) => {
    props.setSelectedRowId(index);
    props.setSelectedIncomeCalendarSynchronization(selectedIncomeCalendarSynchronization);
    props.setShowModal(true);
  };

  return (
    <>
      <Row className='mt-1 pb-1'>
        <h3>Synchronizacja kalendarzy</h3>
      </Row>
      <Row className='mt-1 pb-1'>
        <Col>
          <Button
            variant='success'
            onClick={() => {
              props.setSelectedRowId(-1);
              props.setSelectedIncomeCalendarSynchronization(null);
              props.setShowModal(true);
            }}
          >
            Dodaj kalendarz
          </Button>
        </Col>
      </Row>
      {!props.loading && props.data.length === 0 ? 'Brak synchronizacji kalendarzy' : (<div className='mt-1 pb-1 table-responsive-sm'>
        <table id='income-calendar-synchronization-table' className='table table-hover table-bordered'>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          style: header.column.getCanSort()
                            ? {
                              cursor: 'pointer',
                            }
                            : {},
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <SortUpIcon style={{ marginLeft: '5px' }} />,
                          desc: <SortDownIcon style={{ marginLeft: '5px' }} />,
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {props.loading
              ? Common.Ui.getSkeletonTableRows(6, 5, 35)
              : table.getRowModel().rows.map((row) => (
                <tr 
                  key={row.id}
                  style={{ cursor: 'pointer' }}
                  className={
                    (props.selectedRowId === row.index ? 'selected-row' : undefined) +
                    ' ' +
                    (!row.original.active ? ' inactive' : undefined)
                  }
                  onClick={() => {
                    onTableRowClick(row.index, row.original);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>)}
    </>
  );
}

export default IncomeCalendarsSynchronizationsTable;
